import { useCallback, useEffect, useState } from "react";
import { fetchAdsAttributionOptions, patchAdsSocialPost } from "@/lib/adsAssistantClient";
import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClient";

export function AdsAssistantPostAttributionDialog(props: {
  post: AdsAssistantSocialPostRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { post, onClose, onSaved } = props;
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; display_name: string }[]>([]);
  const [locationId, setLocationId] = useState("");
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await fetchAdsAttributionOptions();
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setLocations(r.data.locations ?? []);
    setUsers(r.data.users ?? []);
  }, []);

  useEffect(() => {
    if (!post) return;
    setLocationId(post.location_id ?? "");
    setUserId(post.ingested_by_user_id ?? "");
    setError(null);
    void load();
  }, [post, load]);

  if (!post) return null;

  async function save() {
    if (!post) return;
    setBusy(true);
    setError(null);
    const r = await patchAdsSocialPost(post.id, {
      location_id: locationId.trim() || null,
      ingested_by_user_id: userId.trim() || null,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-attribution-title"
    >
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-600 dark:bg-zinc-900">
        <h4 id="post-attribution-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Reassign location &amp; credit
        </h4>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Fixes edge cases after import. Credit user drives activity, streaks, and badges; location drives checklist
          filtering.
        </p>
        <div className="mt-3 space-y-2">
          <label className="block text-xs text-gray-600 dark:text-gray-400">
            Location
            <select
              className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">No location</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-gray-400">
            Credit user
            <select
              className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-600"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
